// src/pages/ConsentPage/SignaturePad.js
import React, { useEffect, useRef, useState } from 'react';

/**
 * Finger, stylus or mouse signature capture on a plain canvas.
 * Pointer events cover all three without a library.
 */
export default function SignaturePad({ label, onChange, disabled }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const [hasInk, setHasInk] = useState(false);
    const [unsupported, setUnsupported] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        // Missing on browsers with canvas disabled. Bail out rather than crash
        // the whole consent screen — the examiner can fall back to paper.
        const ctx = canvas && canvas.getContext && canvas.getContext('2d');
        if (!ctx) {
            setUnsupported(true);
            return;
        }
        // Match the backing store to the device pixel ratio, or the line looks
        // blurry and offset on a tablet.
        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        ctx.scale(ratio, ratio);
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#1e3a8a';
    }, []);

    const pointAt = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e) => {
        if (disabled || unsupported) return;
        e.preventDefault();
        canvasRef.current.setPointerCapture(e.pointerId);
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = pointAt(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        drawing.current = true;
    };

    const move = (e) => {
        if (!drawing.current || unsupported) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = pointAt(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const end = () => {
        if (!drawing.current) return;
        drawing.current = false;
        setHasInk(true);
        // PNG on a transparent background, base64 for the API.
        onChange(canvasRef.current.toDataURL('image/png').split(',')[1]);
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext && canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasInk(false);
        onChange(null);
    };

    if (unsupported) {
        return (
            <div className="cn-pad-wrap">
                <div className="cn-pad-head"><span>{label}</span></div>
                <div className="cn-note amber" style={{ margin: '8px 0' }}>
                    This browser cannot capture a signature. Sign on paper, photograph it, and choose
                    &ldquo;Signed on paper&rdquo; as the method.
                </div>
            </div>
        );
    }

    return (
        <div className="cn-pad-wrap">
            <div className="cn-pad-head">
                <span>{label}</span>
                <button type="button" className="cn-clear" onClick={clear} disabled={disabled || !hasInk}>
                    Clear
                </button>
            </div>
            <canvas
                ref={canvasRef}
                className={`cn-pad ${disabled ? 'disabled' : ''}`}
                onPointerDown={start}
                onPointerMove={move}
                onPointerUp={end}
                onPointerLeave={end}
                onPointerCancel={end}
                aria-label={label}
            />
            <div className="cn-pad-line" />
        </div>
    );
}
